import React from "react";
import { BookOpen, FileText, Search, Languages, RefreshCw, ShieldCheck, CheckCircle2, Users, Globe, Award, Quote, Star, ChevronRight, Sparkles, Clock } from "lucide-react";
import { Button } from "../components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { usePopup } from "../context/PopupContext";
import SectionCarousel from "../components/SectionCarousel";
import CTABanner from "../components/CTABanner";
import ChatButton from "../components/ChatButton";
import TestimonialCarousel from "../components/TestimonialCarousel";
import PageMeta from "../components/PageMeta";

const VALUES = [
  { title: "Truthful by Default", desc: "Every sentence is backed by a verifiable secondary source. We won't write fiction." },
  { title: "Policy-First", desc: "We know WP:GNG, WP:BIO, WP:CORP, WP:NPOV and WP:COI inside out." },
  { title: "Long-Term Partners", desc: "We stay with you after publication — maintenance is how pages survive." },
  { title: "Confidential", desc: "Your identity is never linked to our agency in any Wikipedia log." },
];
const TEAM = [
  { name: "Elena Rossi", role: "Head Editor", bio: "12 years as an administrator on English Wikipedia; former academic researcher." },
  { name: "James Okafor", role: "Senior Writer", bio: "Ex-Reuters reporter, specialises in corporate and biographical pages." },
  { name: "Priya Shah", role: "Compliance Lead", bio: "Sources hunter — owns notability reviews and AfC submissions." },
  { name: "Marco Dubois", role: "Localization Lead", bio: "Manages translations across 42 Wikipedia language editions." },
];
const MILESTONES = [
  { year: "2014", text: "Founded as a 3-person editorial collective." },
  { year: "2017", text: "Crossed 100 accepted Wikipedia pages." },
  { year: "2020", text: "Launched multilingual publishing service." },
  { year: "2023", text: "Opened dedicated monitoring desk." },
  { year: "2025", text: "Surpassed 400 live pages worldwide." },
];

export default function About() {
  const { openPopup } = usePopup();
  return (
    <div className="overflow-hidden" data-testid="about-page">
      <PageMeta
        title="About Prime Wiki Services — A Decade of Wikipedia Specialists"
        description="Founded in 2014 by senior Wikipedia administrators. Learn how our editorial standard, policy-first approach and decade of experience have delivered 400+ lasting Wikipedia pages."
        keywords="wikipedia agency, wikipedia editors, wikipedia specialists, about my wiki brand, wikipedia administrators, wikipedia writing team"
      />
      {/* 1. Hero */}
      <section className="mwb-hero-bg px-4 py-16 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-5xl text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700"><Sparkles className="h-3.5 w-3.5" /> About Prime Wiki Services</span>
          <h1 className="font-serif mt-5 text-4xl font-semibold leading-[1.05] text-[#0A2540] sm:text-5xl lg:text-6xl">A team of Wikipedians building legacy, one page at a time.</h1>
          <p className="mt-5 text-base text-slate-600 sm:text-lg">Since 2014 we've specialised in a single thing: creating and protecting Wikipedia pages that stand the test of reviews, time and the wider internet.</p>
        </div>
      </section>

      {/* 1b. Informative intro */}
      <section className="mx-auto max-w-7xl px-4 pt-10 sm:px-6 lg:px-8">
        <div className="grid gap-8 lg:grid-cols-2 lg:items-center">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-600">Our story</p>
            <h2 className="font-serif mt-3 text-3xl font-semibold leading-tight text-[#0A2540] sm:text-4xl">Born out of frustration with vanity agencies.</h2>
            <p className="mt-4 text-base text-slate-600">In 2014, three Wikipedia administrators started this agency after watching hundreds of paid pages get deleted within weeks of publication. They knew the playbook from the inside — and knew that the only way to build a Wikipedia page that survives is to build it the way long-tenured Wikipedians would: sources first, tone second, promotion never.</p>
            <p className="mt-3 text-base text-slate-600">A decade later, we're still run by editors (not marketers), still turn down about 30% of inquiries that don't meet notability, and still make every page we publish defendable line-by-line.</p>
          </div>
          <div className="overflow-hidden rounded-3xl border border-slate-200 shadow-xl">
            <img src="https://images.unsplash.com/photo-1456324504439-367cee3b3c32?auto=format&fit=crop&w=1200&q=80" alt="Rows of books in a quiet research library" className="h-[320px] w-full object-cover" loading="lazy" />
          </div>
        </div>
      </section>

      {/* 2. Stats */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid gap-6 rounded-3xl border border-slate-200 bg-white p-8 sm:grid-cols-4">
          {[{v:"400+",l:"Pages live"},{v:"42",l:"Languages"},{v:"92%",l:"Acceptance rate"},{v:"11yrs",l:"Avg editor tenure"}].map((s)=> (
            <div key={s.l} className="text-center"><div className="font-serif text-4xl font-semibold text-[#0A2540]">{s.v}</div><div className="mt-1 text-sm text-slate-500">{s.l}</div></div>
          ))}
        </div>
      </section>

      {/* 3. Mission */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
        <div className="grid gap-10 lg:grid-cols-2">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-600">Our Mission</p>
            <h2 className="font-serif mt-3 text-3xl font-semibold leading-tight text-[#0A2540] sm:text-4xl">Wikipedia, done right.</h2>
            <p className="mt-4 text-base text-slate-600">We believe Wikipedia is the internet's most important reference. Our mission is to help notable individuals and brands earn their place on it — fairly, transparently and for the long run.</p>
            <ul className="mt-6 space-y-3 text-sm text-slate-700">
              {["Neutral, policy-compliant writing","Sources-first research discipline","Transparent timelines and pricing","Post-publishing care, forever"].map((t)=><li key={t} className="flex items-start gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 text-emerald-500"/>{t}</li>)}
            </ul>
          </div>
          <div className="rounded-3xl bg-[#0A2540] p-8 text-white">
            <Quote className="h-8 w-8 text-emerald-300/70" />
            <p className="font-serif mt-4 text-xl leading-relaxed">"Every engagement begins with a simple question: does Wikipedia's community already consider this subject notable? If the honest answer is no, we say so — and we work with clients to build the record until the answer becomes yes."</p>
            <div className="mt-6 text-sm text-white/80">— The Prime Wiki Services editorial standard</div>
          </div>
        </div>
      </section>

      {/* 4. Values */}
      <section className="bg-slate-50 py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl"><p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-600">Our Values</p><h2 className="font-serif mt-3 text-3xl font-semibold leading-tight text-[#0A2540] sm:text-4xl">What we won't compromise on.</h2></div>
          <SectionCarousel className="mt-10" items={VALUES} gridCols="md:grid-cols-2 lg:grid-cols-4" renderItem={(v)=>(
            <div className="h-full rounded-2xl border border-slate-200 bg-white p-6">
              <div className="grid h-10 w-10 place-items-center rounded-xl bg-[#0A2540] text-white"><ShieldCheck className="h-5 w-5"/></div>
              <h3 className="font-serif mt-4 text-lg text-[#0A2540]">{v.title}</h3><p className="mt-2 text-sm text-slate-600">{v.desc}</p>
            </div>
          )} />
        </div>
      </section>

      {/* 5. Milestones */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
        <div className="max-w-2xl"><p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-600">Milestones</p><h2 className="font-serif mt-3 text-3xl font-semibold leading-tight text-[#0A2540] sm:text-4xl">A decade of building credibility.</h2></div>
        <div className="mt-10 grid gap-4 md:grid-cols-5">
          {MILESTONES.map((m)=>(
            <div key={m.year} className="rounded-2xl border border-slate-200 bg-white p-5">
              <div className="font-serif text-3xl text-emerald-500">{m.year}</div>
              <p className="mt-2 text-sm text-slate-600">{m.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* 6. Team section removed per spec */}
      <TestimonialCarousel eyebrow="Testimonials" title="Clients who chose credibility." subtitle="Real pages. Real clients. Real longevity." />

      {/* 6b. Team section removed per spec */}

      {/* 7. How We Work */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
        <div className="max-w-2xl"><p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-600">How We Work</p><h2 className="font-serif mt-3 text-3xl font-semibold leading-tight text-[#0A2540] sm:text-4xl">Transparent, evidence-based, paced.</h2></div>
        <SectionCarousel className="mt-10" items={[
          {icon:Search,title:"Deep source research",desc:"We scout secondary sources across 50+ publications before we write a line."},
          {icon:FileText,title:"Collaborative drafts",desc:"You review each draft; we iterate until it is factually tight and neutrally phrased."},
          {icon:BookOpen,title:"Wikipedia submission",desc:"We submit through AfC and handle reviewer dialogue in your absence."},
          {icon:RefreshCw,title:"Protect & maintain",desc:"We watch for edits, defend against vandalism, and update your page quarterly."},
        ]} gridCols="md:grid-cols-2 lg:grid-cols-4" renderItem={(h)=>(
          <div className="h-full rounded-2xl border border-slate-200 bg-white p-6">
            <div className="grid h-10 w-10 place-items-center rounded-xl bg-emerald-50 text-emerald-600"><h.icon className="h-5 w-5"/></div>
            <h3 className="font-serif mt-4 text-lg text-[#0A2540]">{h.title}</h3><p className="mt-2 text-sm text-slate-600">{h.desc}</p>
          </div>
        )} />
      </section>

      {/* 8. Global coverage */}
      <section className="bg-slate-50 py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid gap-10 lg:grid-cols-2 items-center">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-600">Global reach</p>
            <h2 className="font-serif mt-3 text-3xl font-semibold leading-tight text-[#0A2540] sm:text-4xl">Clients on six continents, pages in 42 languages.</h2>
            <p className="mt-4 text-base text-slate-600">Whether you're a North American founder, a European author or an APAC enterprise, our editors match the tone, sourcing norms and cultural context of each regional Wikipedia community.</p>
          </div>
          <div className="grid grid-cols-3 gap-3">
            {["North America","Europe","MENA","APAC","LATAM","Africa"].map((r)=>(
              <div key={r} className="rounded-2xl border border-slate-200 bg-white p-4 text-center">
                <Globe className="mx-auto h-5 w-5 text-emerald-500"/>
                <div className="mt-2 text-sm font-semibold text-[#0A2540]">{r}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 9. Why us */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
        <div className="max-w-2xl"><p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-600">Why us</p><h2 className="font-serif mt-3 text-3xl font-semibold leading-tight text-[#0A2540] sm:text-4xl">Other agencies vs Prime Wiki Services.</h2></div>
        <div className="mt-10 grid gap-5 md:grid-cols-2">
          <div className="rounded-2xl border border-slate-200 bg-white p-6">
            <h3 className="font-serif text-xl text-slate-600">Typical agencies</h3>
            <ul className="mt-3 space-y-2 text-sm text-slate-600">
              <li>• Black-box pricing, vague timelines</li>
              <li>• Content farm writing, no Wikipedia experience</li>
              <li>• Disappear after publishing</li>
              <li>• Pages often reversed within weeks</li>
            </ul>
          </div>
          <div className="rounded-2xl border border-emerald-300 bg-emerald-50/40 p-6">
            <h3 className="font-serif text-xl text-[#0A2540]">Prime Wiki Services</h3>
            <ul className="mt-3 space-y-2 text-sm text-slate-700">
              <li className="flex gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 text-emerald-500"/>Flat pricing, weekly updates</li>
              <li className="flex gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 text-emerald-500"/>Only senior Wikipedians on projects</li>
              <li className="flex gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 text-emerald-500"/>12 months monitoring included</li>
              <li className="flex gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 text-emerald-500"/>Pages engineered to last years</li>
            </ul>
          </div>
        </div>
      </section>

      {/* 10. Claudia Fernandez testimonial — removed per spec */}

      {/* 11. FAQ */}
      <section className="bg-slate-50 py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-10 lg:grid-cols-12 lg:items-start">
            <div className="lg:col-span-7">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-600">FAQ</p>
              <h2 className="font-serif mt-3 text-3xl font-semibold leading-tight text-[#0A2540] sm:text-4xl">Common questions about us.</h2>
              <p className="mt-3 max-w-xl text-base text-slate-600">How we're structured, who's on the team, and how we handle the ethics of paid Wikipedia work.</p>
              <Accordion type="single" collapsible className="mt-8 space-y-3">
                {[{q:"Where is your team based?",a:"We are a distributed team across New York, London, Dubai and Singapore, with editors native to each language region we serve."},
                  {q:"Do you have Wikipedia administrators on staff?",a:"Yes, our head editor is a long-term admin on English Wikipedia and several contractors hold advanced user rights."},
                  {q:"Can you share client references?",a:"After a signed NDA and client approval, we can arrange warm intros with past clients whose industries are similar to yours."},
                  {q:"Do you do paid editing disclosures?",a:"Absolutely. Per Wikipedia's Terms of Use we disclose paid work on each editor's user page where required."}].map((i,idx)=>(
                  <AccordionItem key={idx} value={`a-${idx}`} className="overflow-hidden rounded-2xl border border-slate-200 bg-white px-6"><AccordionTrigger className="text-left text-base font-semibold text-[#0A2540]">{i.q}</AccordionTrigger><AccordionContent className="text-sm text-slate-600">{i.a}</AccordionContent></AccordionItem>
                ))}
              </Accordion>
            </div>

            <div className="lg:col-span-5">
              <div className="relative h-full min-h-[440px] overflow-hidden rounded-3xl border border-slate-200 shadow-xl">
                <img
                  src="https://images.unsplash.com/photo-1521791136064-7986c2920216?auto=format&fit=crop&w=1200&q=80"
                  alt="A handshake between a client and an editorial lead after a successful consultation"
                  className="absolute inset-0 h-full w-full object-cover"
                  loading="lazy"
                />
                <div aria-hidden className="absolute inset-0 bg-gradient-to-t from-[#0A2540]/70 via-[#0A2540]/10 to-transparent" />
                <div className="absolute bottom-5 left-5 right-5 rounded-2xl bg-white/95 p-4 shadow-lg backdrop-blur">
                  <div className="text-xs font-semibold uppercase tracking-wider text-emerald-600">Built on trust</div>
                  <div className="mt-1 font-serif text-base text-[#0A2540]">NDA-protected engagements, senior editors and transparent pricing.</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 12. Culture */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 grid gap-10 lg:grid-cols-2 items-center">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-600">Culture</p>
          <h2 className="font-serif mt-3 text-3xl font-semibold leading-tight text-[#0A2540] sm:text-4xl">We treat Wikipedia as a public good.</h2>
          <p className="mt-4 text-base text-slate-600">We contribute to the free knowledge movement, respect community norms and turn down clients who don't meet notability. That's how we protect the long-term integrity of your page.</p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {[{i:Users,t:"Remote-first",s:"28 editors, 4 continents"},{i:Clock,t:"Async culture",s:"Deep work, no rush"},{i:Award,t:"Policy pride",s:"We read Wikipedia policy every week"},{i:ShieldCheck,t:"Zero shortcuts",s:"No sockpuppets, ever"}].map((c)=>(
            <div key={c.t} className="rounded-2xl border border-slate-200 bg-white p-5"><c.i className="h-5 w-5 text-emerald-500"/><div className="mt-3 font-serif text-lg text-[#0A2540]">{c.t}</div><div className="text-sm text-slate-600">{c.s}</div></div>
          ))}
        </div>
      </section>

      {/* 13. CTA */}
      <CTABanner title="Ready to write your chapter?" subtitle="Book a free notability review with our team and get honest feedback on your eligibility within 24 hours." />

      {/* 14. Footer teaser */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 text-center">
        <h3 className="font-serif text-2xl text-[#0A2540]">Prefer a chat first?</h3>
        <p className="mt-2 text-sm text-slate-600">Our specialists answer within minutes during business hours.</p>
        <div className="mt-5 flex justify-center gap-3">
          <Button onClick={openPopup} className="rounded-full bg-[#0A2540] px-6 text-sm font-semibold text-white hover:bg-[#123B6E]">Start a project</Button>
          <ChatButton className="rounded-full border border-slate-200 bg-white px-6 py-2.5 text-sm font-semibold text-[#0A2540]" />
        </div>
      </section>

      {/* 15. Next steps */}
      <section className="mx-auto max-w-7xl px-4 pb-20 sm:px-6 lg:px-8">
        <div className="flex flex-wrap items-center justify-between gap-4 rounded-3xl border border-slate-200 bg-slate-50 p-6">
          <div><h3 className="font-serif text-lg text-[#0A2540]">Want our detailed case book?</h3><p className="text-sm text-slate-600">A confidential PDF with 8 anonymised case studies.</p></div>
          <Button onClick={openPopup} className="rounded-full bg-emerald-500 hover:bg-emerald-400">Request PDF <ChevronRight className="ml-1 h-4 w-4"/></Button>
        </div>
      </section>
    </div>
  );
}
